/**
 * ItemDetailEditable Component
 *
 * Unified item detail view with inline editing per section.
 * Each section is always visible. If the user has permission, they can
 * edit that section directly — no global edit mode toggle.
 * Changes auto-save on blur or confirmation.
 */

import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { isFeatureEnabled, FEATURE_FLAGS } from '../../../lib/featureFlags';
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  GitBranch,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemImpactStore } from '../../../stores/prioritization/itemImpactStore';
import { useItemClassificationStore } from '../../../stores/prioritization/itemClassificationStore';
import { responsesToScopingInputs, type AltitudeQuestionResponse } from '../../../lib/altitudeQuestions';
import { getWorkspaceModules, isModuleEnabled } from '../../../lib/workspaceModulesService';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { checkPolicyForItem } from '../../../lib/authorization/storeEnforcement';
import { computeAltitudeLevel } from '../../../lib/altitudePrediction';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';
import {
  resolveItemEffortCapacityUnits,
  WORK_SIZE_BAND_LABELS,
  effortFieldsFromAltitudeLevel,
  effortFieldsFromWorkSize,
  type WorkSizeBand,
} from '../../../lib/itemToPlanningAdapter';
import { usePlanningDefaultsStore } from '../../../stores/prioritization/planningDefaultsStore';
import { CandidateTypeSelector } from '../validation';
import { ExecutionProfileChip } from '../ItemClassificationBadge';
import type { ExecutionProfile } from '../../../types/prioritization/itemClassification';
import { ItemFrameSelector, CreateFrameModal } from '../frames';
import { EditFrameModal } from '../frames/EditFrameModal';
import { ScopingQuestionnaire } from '../leveling';
import { LineageSummary } from '../LineageSummary';
import { getItemCategoryLabel } from '../TrackCellBadge';
import { ItemRelationshipActions } from './ItemRelationshipActions';
import { RelationshipDiscoverySheet } from './RelationshipDiscoverySheet';
import { CreateCanonicalClusterModal } from './CreateCanonicalClusterModal';
import { CreatePackageModal } from './CreatePackageModal';
import { CreateExclusiveModal } from './CreateExclusiveModal';
import { AddContainsModal } from './AddContainsModal';
import { AddSequenceLinkModal } from './AddSequenceLinkModal';
import { AddCompositionModal } from './AddCompositionModal';
import { AddElaborationModal } from './AddElaborationModal';
import { ProvenanceActionModal } from './ProvenanceActionModal';
import { AddToCollectionModal } from './AddToCollectionModal';
import { ValuesTab } from './activity/ValuesTab';
import { CustomFieldsSection } from './CustomFieldsSection';
import { ItemVisibilitySection } from './ItemVisibilitySection';
import { InfoModal, InfoButton } from '../InfoModal';
import { ALTITUDE_GUIDE_SECTIONS, CATEGORY_GUIDE_SECTIONS } from './itemClassificationGuides';
import { AuthorizationGate } from '../AuthorizationGate';
import type { Candidate } from '../../../types/prioritization/lifecycle';
import type { CandidateLanguageType } from '../../../types/prioritization';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { AltitudeScopingInputs } from '../../../types/prioritization/leveling';
import {
  type PlanningMaturity,
  PLANNING_MATURITY_LABELS,
  PLANNING_MATURITY_DESCRIPTIONS,
  PLANNING_MATURITY_ORDER,
} from '../../../types/prioritization/itemLifecycle';

// ============================================================================
// Constants
// ============================================================================

/** Where an item's effort came from, in plain words. Also used by the item page's effort rows. */
// eslint-disable-next-line react-refresh/only-export-components
export function describeItemEffortSource(
  effort: ReturnType<typeof resolveItemEffortCapacityUnits>,
  altitudeLevel: number | null | undefined,
): string {
  if (effort.capacityUnits == null) {
    return 'Not yet estimated — set Work Size or Altitude above, or size it directly in Planning.';
  }
  switch (effort.source) {
    case 'explicit':
      return 'Entered directly as Item Effort.';
    case 'planner_override':
      return 'Set by a planner override in Planning.';
    case 'work_size':
      return `From Work Size (${effort.workSizeBand ? WORK_SIZE_BAND_LABELS[effort.workSizeBand as WorkSizeBand] : '—'}).`;
    case 'altitude_level_inferred':
      return `Estimated from Altitude Level ${altitudeLevel ?? '—'} — no Work Size answered.`;
    default:
      return 'Source unknown.';
  }
}

// ============================================================================
// Section wrapper — clean, flat, no accent bars
// ============================================================================

function Section({
  label,
  children,
  action,
}: {
  label: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-[var(--text-pri)]">{label}</label>
        {action}
      </div>
      {children}
    </div>
  );
}

// ============================================================================
// ItemTextField — a free-text item field saved when the person leaves the box
// ============================================================================

function ItemTextField({
  anchorId,
  label,
  placeholder,
  value,
  readOnly,
  onSave,
}: {
  anchorId: string;
  label: string;
  placeholder: string;
  value: string;
  readOnly: boolean;
  onSave: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div id={anchorId} className="scroll-mt-24">
      <label htmlFor={`${anchorId}-input`} className="block text-sm font-semibold text-[var(--text-pri)] mb-2">{label}</label>
      {readOnly ? (
        <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap">
          {value || <span className="text-[var(--text-ter)] italic">Not set</span>}
        </p>
      ) : (
        <textarea
          id={`${anchorId}-input`}
          rows={2}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const next = draft.trim();
            if (next !== value.trim()) onSave(next === '' ? null : next);
          }}
          className="w-full text-sm px-3 py-2 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-1 focus:ring-[var(--slate)]"
        />
      )}
    </div>
  );
}

// ============================================================================
// Section card — matches Attributes screen (card, title, optional subtitle)
// ============================================================================

function SectionCard({
  sectionId,
  title,
  subtitle,
  children,
}: {
  sectionId?: string;
  title?: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div id={sectionId} className="card">
      {(title || subtitle) && (
        <div className="mb-4">
          {title && <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>}
          {subtitle && <p className="text-sm text-[var(--text-ter)] mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}


// ============================================================================
// Impact Callout — calibration link between predicted CoD and actual evidence
// ============================================================================

const EMPTY_ARRAY: never[] = [];

function ImpactCallout({ itemId }: { itemId: string }) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const count = useItemImpactStore((s) => (s.eventsByItem[itemId] ?? EMPTY_ARRAY).length);
  const [moduleEnabled, setModuleEnabled] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    getWorkspaceModules(workspaceId).then(({ data }) => {
      if (!cancelled) setModuleEnabled(isModuleEnabled(data, 'cost_of_delay'));
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!moduleEnabled || count === 0) return null;

  return (
    <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
      <TrendingDown className="h-3.5 w-3.5 flex-shrink-0 text-[var(--mustard)]" />
      <span>
        <span className="font-semibold">{count} actual impact event{count !== 1 ? 's' : ''}</span>
        {' '}recorded against this item.{' '}
        <span className="text-[var(--mustard)]">Open the Impact tab to review — this calibrates your cost of delay estimate.</span>
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export type ItemDetailSection = 'classification' | 'relationships' | 'values' | 'lineage';

interface ItemDetailEditableProps {
  item: Candidate;
  onSaved?: () => void;
  onCompleted?: () => void;
  onDeleted?: () => void;
  /**
   * Retire handled by parent (governed retire_new_item — never a hard delete,
   * owner ruling 2026-09-22); shown under Classification → Exit status.
   */
  onRetireItem?: () => void;
  /** When true, all shaping fields and actions are read-only (view mode) */
  readOnly?: boolean;
  /**
   * When provided, only the listed sections are visible (others are CSS-hidden).
   * The component stays mounted so modal state persists across tab switches.
   * If omitted, all sections are shown (legacy full-page mode).
   */
  visibleSections?: ItemDetailSection[];
}

// ============================================================================
// Guide content
// ============================================================================

import { TrendingDown, Archive } from 'lucide-react';


// ============================================================================

// ─── SubjectTypeSection ───────────────────────────────────────────────────────
// Optional, collapsible. Collapsed by default unless a value is already set.

const META_CATEGORY_OPTIONS = [
  { value: 'people',      label: 'People' },
  { value: 'places',      label: 'Places' },
  { value: 'things',      label: 'Things' },
  { value: 'activities',  label: 'Activities' },
  { value: 'information', label: 'Information' },
  { value: 'time',        label: 'Time' },
] as const;

function SubjectTypeSection({
  item,
  readOnly,
  saveField,
}: {
  item: import('../../../types/prioritization/lifecycle').Candidate;
  readOnly: boolean;
  saveField: (updates: Record<string, unknown>) => void;
}) {
  const [expanded, setExpanded] = useState(() => !!item.metaCategory);

  return (
    <div className="border-t border-[var(--rule)] pt-4">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors w-full text-left group"
      >
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-sec)]" />
          : <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-sec)]" />
        }
        <span className="font-medium text-[var(--text-sec)]">Subject type</span>
        <span className="text-xs text-[var(--text-ter)] ml-1">— optional</span>
        {item.metaCategory && !expanded && (
          <span className="ml-auto text-xs font-medium text-[var(--text-ter)] capitalize">{item.metaCategory}</span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[var(--text-ter)]">
            Tag this item with its subject type so it can be found and filtered when creating a prioritization session.
          </p>
          {!readOnly ? (
            <div className="flex flex-wrap gap-2">
              {META_CATEGORY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const newVal = item.metaCategory === opt.value ? undefined : opt.value;
                    saveField({ metaCategory: newVal ?? null });
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    item.metaCategory === opt.value
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-sec)]">
              {item.metaCategory
                ? item.metaCategory.charAt(0).toUpperCase() + item.metaCategory.slice(1)
                : <span className="text-[var(--text-ter)] italic">Not set</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function ItemDetailEditable({ item, onSaved, onCompleted: _onCompleted, onDeleted: _onDeleted, onRetireItem, readOnly = false, visibleSections }: ItemDetailEditableProps) {
  // When visibleSections is provided, we CSS-hide sections not in the list.
  // The component stays fully mounted so modal state survives tab switches.
  const show = (s: ItemDetailSection) =>
    !visibleSections || visibleSections.includes(s);
  const altitudeText = useAltitudeText();
  // Inline-edit state (synced from item on mount / item change)
  const [candidateType, setCandidateType] = useState<CandidateLanguageType | undefined>(item.itemCategory);
  const [altitudeLevel, setAltitudeLevel] = useState<AltitudeLevel | undefined>(item.altitudeLevel);
  const [scopingInputs, setScopingInputs] = useState<AltitudeScopingInputs>(item.altitudeScopingInputs || {});
  const itemEffortBandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);
  const itemEffort = useMemo(
    () => resolveItemEffortCapacityUnits(item, itemEffortBandHours),
    [item, itemEffortBandHours],
  );

  // Derive AI-sourced scoping inputs from the most recently accepted ATC proposal
  // so the questionnaire can highlight those values in violet.
  const classificationHistory = useItemClassificationStore(
    (s) => s.byItem[item.id]?.history ?? EMPTY_ARRAY,
  );
  const aiSetScopingInputs = useMemo(() => {
    const latestAtcAccepted = classificationHistory.find(
      (p) => p.status === 'accepted' && p.proposedByAgent === 'atc',
    );
    if (!latestAtcAccepted) return undefined;
    const responses = latestAtcAccepted.classificationFields.questionResponses;
    if (!Array.isArray(responses) || responses.length === 0) return undefined;
    return responsesToScopingInputs(responses as AltitudeQuestionResponse[]);
  }, [classificationHistory]);

  const [showCreateFrameModal, setShowCreateFrameModal] = useState(false);
  const [pendingCategoryType, setPendingCategoryType] = useState<CandidateLanguageType | undefined>(undefined);
  const [editingFrameId, setEditingFrameId] = useState<string | null>(null);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showCategoryGuide, setShowCategoryGuide] = useState(false);
  const [showAltitudeGuide, setShowAltitudeGuide] = useState(false);
  const [altitudeAttempted, setAltitudeAttempted] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showExclusiveModal, setShowExclusiveModal] = useState(false);
  const [showContainsModal, setShowContainsModal] = useState(false);
  const [showSequenceLinkModal, setShowSequenceLinkModal] = useState(false);
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [showCompositionModal, setShowCompositionModal] = useState(false);
  const [showElaborationModal, setShowElaborationModal] = useState(false);
  const [showProvenanceActionModal, setShowProvenanceActionModal] = useState(false);
  const [showDiscoverySheet, setShowDiscoverySheet] = useState(false);

  // Re-sync local state when the item changes from outside
  useEffect(() => {
    setCandidateType(item.itemCategory);
    setAltitudeLevel(item.altitudeLevel);
    setScopingInputs(item.altitudeScopingInputs || {});
    setAltitudeAttempted(false);
  }, [item.id, item.itemCategory, item.altitudeLevel, item.altitudeScopingInputs]);

  // Stores
  // Subscribe to effective frame type for altitude - computed in selector so we re-render as soon as user selects a different frame
  const effectiveFrameTypeForAltitude = useItemsStore((s) => {
    const it = s.items.find((i) => i.id === item.id) ?? item;
    if (it.canonicalFrameId && Array.isArray(it.frames) && it.frames.length > 0) {
      const frame = it.frames.find((f: { id?: string }) => f.id === it.canonicalFrameId);
      // Handle both camelCase (in-memory) and snake_case (from DB) - fromDbFormat does not recurse into nested objects
      const frameTypeVal = frame?.frameType ?? (frame as { frame_type?: string })?.frame_type;
      if (frameTypeVal) return frameTypeVal as CandidateLanguageType;
    }
    return (it.itemCategory ?? candidateType) as CandidateLanguageType | undefined;
  });
  const updateItem = useItemsStore((s) => s.updateItem);
  const setUseItemAsCanonical = useItemsStore((s) => s.setUseItemAsCanonical);
  const setCanonicalFrame = useItemsStore((s) => s.setCanonicalFrame);
  const saveFrameAltitude = useItemsStore((s) => s.saveFrameAltitude);
  const addFrame = useItemsStore((s) => s.addFrame);
  // Get the active canonical frame ID so we can persist altitude to it
  const canonicalFrameId = useItemsStore((s) => {
    const it = s.items.find((i) => i.id === item.id);
    return it?.canonicalFrameId;
  });
  // Get all frames so we can seed the altitude cache from DB on load
  const itemFrames = useItemsStore((s) => {
    const it = s.items.find((i) => i.id === item.id);
    return it?.frames;
  });
  const addNotification = useUIStore((s) => s.addNotification);
  const refreshRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const itemDataForPolicy = useMemo(() => {
    // Candidate type doesn't surface all DB columns; cast required for policy normalization.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const i = item as any;
    return {
      ...item,
      createdBy: i.created_by_user_id ?? i.createdBy,
      assigneeId: i.shaping_owner_id ?? i.shapingOwnerId ?? i.steward_id ?? i.stewardId,
      ownerId: i.owner_id ?? i.ownerId,
      workspaceId: i.workspaceId ?? i.workspace_id,
      exit_status: i.exit_status ?? 'active',
      detailedState: i.detailedState ?? i.detailed_state,
      lifecycle_state: i.lifecycle_state ?? i.lifecycleState,
    };
  }, [item]);

  // Capabilities are loaded asynchronously and arrive after first render. Without
  // them in the dependency list this memo kept the answer it computed while the
  // set was still empty — the person was told "no" for the life of the page even
  // once their capabilities landed, which is what left Classification (and so the
  // altitude questionnaire, Work Size and every effort estimate) read-only for an
  // Item Manager.
  const capabilitySignature = useUserStore(
    (state) => `${state.permissionsLoaded}:${state.resolvedCapabilityIds.length}:${state.assignedRoleNames.join(',')}`,
  );
  const perms = useMemo(() => {
    // checkPolicyForItem reads the capability set from the store rather than
    // from an argument, so the linter cannot see the dependency. Name it here.
    void capabilitySignature;
    const data = itemDataForPolicy as Record<string, unknown>;
    const action = 'items.edit';
    return {
      core: checkPolicyForItem(action, item.id, data).allowed,
      category: checkPolicyForItem(action, item.id, data).allowed,
      altitude: checkPolicyForItem(action, item.id, data).allowed,
      leveling: checkPolicyForItem(action, item.id, data).allowed,
      frames: checkPolicyForItem(action, item.id, data).allowed,
    };
  }, [item.id, itemDataForPolicy, capabilitySignature]);

  const canEditAnything = useMemo(() => !readOnly && Object.values(perms).some(Boolean), [readOnly, perms]);

  // ---- Inline save: persist a single field change immediately ----

  const saveField = useCallback((
    updates: Record<string, unknown>,
    _eventType?: 'item_category_updated' | 'item_altitude_updated' | 'item_parent_updated',
  ) => {
    // Readiness is now contextual (per cycle/track) and computed on demand.
    // We no longer write readyForRanking or shapingCompleteness here — those
    // are only meaningful in the context of a specific cycle/session gate check.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateItem(item.id, updates as any);
    // Field-change and state-transition events are logged once by itemsStore.updateItem (per-field logShapingEvent + item_transition when detailedState changes). Do not log again here to avoid duplicate history rows.
    onSaved?.();
  }, [item.id, updateItem, onSaved]);

  // Local cache: preserve altitude answers per category type so switching back restores them.
  // Keyed by category type string (e.g. "problem", "risk").
  const altitudeCacheRef = useRef<Record<string, { inputs: AltitudeScopingInputs; level?: AltitudeLevel }>>({});

  // Seed the cache from DB: item-level altitude + any frames that have altitude data.
  // This runs on item load so cached values survive page reloads.
  useEffect(() => {
    if (item.itemCategory && item.altitudeScopingInputs && Object.keys(item.altitudeScopingInputs).length > 0) {
      altitudeCacheRef.current[item.itemCategory] = {
        inputs: item.altitudeScopingInputs,
        level: item.altitudeLevel,
      };
    }
    // Also seed from frames that have persisted altitude data
    if (itemFrames) {
      for (const frame of itemFrames) {
        if (frame.altitudeScopingInputs && Object.keys(frame.altitudeScopingInputs).length > 0) {
          altitudeCacheRef.current[frame.frameType] = {
            inputs: frame.altitudeScopingInputs as AltitudeScopingInputs,
            level: (frame.altitudeLevel as AltitudeLevel) ?? undefined,
          };
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]); // only on item load, not on every render

  // Keep current altitude values in refs so the frame-switch effect can stash them
  // without needing them as dependencies (which would cause spurious re-runs).
  const currentScopingRef = useRef(scopingInputs);
  const currentAltitudeLevelRef = useRef(altitudeLevel);
  currentScopingRef.current = scopingInputs;
  currentAltitudeLevelRef.current = altitudeLevel;

  // When effective frame type changes (category switch or frame switch), save outgoing
  // answers to cache and restore incoming answers from cache.
  const prevEffectiveFrameTypeRef = useRef(effectiveFrameTypeForAltitude);
  useEffect(() => {
    const prev = prevEffectiveFrameTypeRef.current;
    prevEffectiveFrameTypeRef.current = effectiveFrameTypeForAltitude;

    if (prev === effectiveFrameTypeForAltitude || prev === undefined) return;

    // Stash outgoing altitude answers
    const outgoing = currentScopingRef.current;
    if (prev && Object.keys(outgoing).length > 0) {
      altitudeCacheRef.current[prev] = { inputs: outgoing, level: currentAltitudeLevelRef.current };
    }

    // Restore incoming altitude answers (if we have cached values for this type)
    const cached = effectiveFrameTypeForAltitude ? altitudeCacheRef.current[effectiveFrameTypeForAltitude] : undefined;
    if (cached) {
      setScopingInputs(cached.inputs);
      setAltitudeLevel(cached.level);
    } else {
      setScopingInputs({});
      setAltitudeLevel(undefined);
    }
  }, [effectiveFrameTypeForAltitude]);

  // =========================================================================
  // RENDER — single unified view, inline-editable per section
  // =========================================================================

  // Cast once for extended DB columns not present in the Candidate type contract.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemExt = item as any;
  const _stateKey = itemExt.exit_status || 'active';
  const stateLabel = ({ active: 'Active', deferred: 'Deferred', archived: 'Archived' } as Record<string, string>)[_stateKey] || _stateKey;
  // Prefer the human-readable name; only fall back to UUID if nothing else is available.
  return (
    <div className="space-y-8">
      {/* 1. Classification */}
      <div className={show('classification') ? undefined : 'hidden'}>
      <SectionCard sectionId="item-section-classification" title="Classification" subtitle="Item category, altitude, and additional categories.">
        <div className="space-y-5">
          <Section label="Item Category" action={<InfoButton onClick={() => setShowCategoryGuide(true)} title="What do the item categories mean?" />}>
            {perms.category && !readOnly ? (
              <>
                <p className="text-xs text-[var(--text-ter)] mb-2">
                  How is this item framed: problem, opportunity, risk, solution, commitment, or investment.
                </p>
                <CandidateTypeSelector
                  value={effectiveFrameTypeForAltitude ?? candidateType}
                  onChange={(newType) => {
                    if (newType === undefined || !candidateType) {
                      // Clearing, or setting for the first time — save directly, no modal needed
                      setCandidateType(newType);
                      setUseItemAsCanonical(item.id);
                      saveField({ itemCategory: newType }, 'item_category_updated');
                      return;
                    }
                    if (newType === (effectiveFrameTypeForAltitude ?? candidateType)) return;

                    // If switching to a category that already has all 3 altitude answers (cached from
                    // a prior selection in this session or seeded from DB), or that has an existing
                    // alternate frame, skip the modal and switch directly — no need to reframe again.
                    const cachedAnswers = altitudeCacheRef.current[newType];
                    const hasFullAltitudeCache = !!(
                      cachedAnswers?.inputs?.timeHorizon &&
                      cachedAnswers?.inputs?.workSize &&
                      cachedAnswers?.inputs?.impactSurface
                    );
                    const existingFrame = (itemFrames ?? []).find((f) => {
                      const ft = f.frameType ?? (f as { frame_type?: string }).frame_type;
                      return ft === newType;
                    });

                    if (hasFullAltitudeCache || existingFrame) {
                      // Switch to this frame/category directly without showing the modal
                      if (existingFrame) {
                        setCanonicalFrame(item.id, existingFrame.id);
                      } else {
                        // Going back to the item's original category — reset canonical to item-own
                        setUseItemAsCanonical(item.id);
                        setCandidateType(newType);
                        saveField({ itemCategory: newType }, 'item_category_updated');
                      }
                      return;
                    }

                    // Changing to a new category with no prior setup — open the reframe modal
                    setPendingCategoryType(newType);
                    setShowCreateFrameModal(true);
                  }}
                />
              </>
            ) : (
              <p className="text-sm text-[var(--text-sec)]">{getItemCategoryLabel(item.itemCategory) || <span className="text-[var(--text-ter)] italic">Not set</span>}</p>
            )}
          </Section>

          <Section label="Additional Categories">
            <ItemFrameSelector
              candidateId={item.id}
              compact
              onCreateFrame={() => {
                setPendingCategoryType(undefined);
                setShowCreateFrameModal(true);
              }}
              onEditFrame={(frameId) => setEditingFrameId(frameId)}
            />
          </Section>

          <Section label="Altitude" action={<InfoButton onClick={() => setShowAltitudeGuide(true)} title="What do the altitude levels mean?" />}>
            {perms.altitude && !readOnly ? (
              <>
                {!candidateType && altitudeAttempted && (
                  <p className="text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    Select an Item Category above before setting altitude.
                  </p>
                )}
                <div className={candidateType ? '' : 'relative opacity-50'}>
                  {!candidateType && (
                    <div
                      className="absolute inset-0 z-10 cursor-not-allowed"
                      onClick={() => setAltitudeAttempted(true)}
                    />
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-[var(--text-ter)]">Computed from time horizon, work size, and impact surface.</p>
                    {altitudeLevel && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hover-tint)] text-[var(--text-pri)] text-sm font-semibold" title={`Altitude ${altitudeLevel}`}>
                        {altitudeText.format(altitudeLevel, item.itemCategory ?? candidateType)}
                      </span>
                    )}
                  </div>
                  <ScopingQuestionnaire
                    key={effectiveFrameTypeForAltitude ?? 'default'}
                    value={scopingInputs}
                    onChange={(inputs) => {
                      setScopingInputs(inputs);
                      const computed = computeAltitudeLevel(inputs);
                      setAltitudeLevel(computed);
                      // Keep the local cache in sync so switching away and back restores answers
                      if (effectiveFrameTypeForAltitude) {
                        altitudeCacheRef.current[effectiveFrameTypeForAltitude] = { inputs, level: computed };
                      }
                      // Persist to item (backward compat + readiness calc).
                      // Intentional workSize seeds Item Effort (CU) unless planner/explicit override.
                      const workSizeEffort =
                        inputs.workSize
                          ? effortFieldsFromWorkSize(
                              inputs.workSize,
                              item.effortSource,
                              itemEffortBandHours,
                            )
                          : null;
                      saveField(
                        {
                          altitudeLevel: computed,
                          altitudeScopingInputs: inputs,
                          altitudeLifecycleState: 'proposed',
                          ...(workSizeEffort ?? {}),
                        },
                        'item_altitude_updated',
                      );

                      // If all 3 scoping inputs are answered, persist altitude to the active frame.
                      // Never auto-create a frame whose type matches the item's own itemCategory —
                      // the item itself already represents that type and creating one produces visible duplicates.
                      const allAnswered = inputs.timeHorizon && inputs.workSize && inputs.impactSurface;
                      const isOwnCategory = effectiveFrameTypeForAltitude === (item.itemCategory ?? candidateType);
                      if (allAnswered && effectiveFrameTypeForAltitude && !isOwnCategory) {
                        const existingFrame = (itemFrames ?? []).find(
                          (f) => f.frameType === effectiveFrameTypeForAltitude
                        );
                        if (existingFrame) {
                          // Frame exists — just save altitude to it
                          saveFrameAltitude(item.id, existingFrame.id, inputs as Record<string, string>, computed);
                        } else {
                          // Create a new frame for this category with altitude baked in
                          addFrame(item.id, {
                            frameType: effectiveFrameTypeForAltitude,
                            title: item.title,
                            description: item.description ?? '',
                            createdBy: 'system',
                            isCanonical: true,
                            altitudeScopingInputs: inputs as Record<string, string>,
                            altitudeLevel: computed,
                          });
                        }
                      } else if (canonicalFrameId) {
                        // Partial answers — still persist to existing frame if active
                        saveFrameAltitude(item.id, canonicalFrameId, inputs as Record<string, string>, computed);
                      }
                    }}
                    onAltitudeComputed={setAltitudeLevel}
                    directAltitudeValue={altitudeLevel}
                    onDirectAltitude={(level) => {
                      setAltitudeLevel(level);
                      setScopingInputs({});
                      if (effectiveFrameTypeForAltitude) {
                        altitudeCacheRef.current[effectiveFrameTypeForAltitude] = { inputs: {}, level };
                      }
                      const inferred =
                        item.effortSource === 'planner_override' || item.effortSource === 'explicit'
                          ? null
                          : effortFieldsFromAltitudeLevel(level, itemEffortBandHours);
                      saveField(
                        {
                          altitudeLevel: level,
                          altitudeScopingInputs: {},
                          altitudeLifecycleState: 'accepted',
                          ...(inferred ?? {}),
                        },
                        'item_altitude_updated',
                      );
                      if (canonicalFrameId) {
                        saveFrameAltitude(item.id, canonicalFrameId, {}, level);
                      }
                    }}
                    candidateType={effectiveFrameTypeForAltitude}
                    aiSetScopingInputs={aiSetScopingInputs}
                    compact
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-[var(--text-sec)]">{item.altitudeLevel ? <span title={`Altitude ${item.altitudeLevel}`}>{altitudeText.format(item.altitudeLevel, item.itemCategory)}</span> : <span className="text-[var(--text-ter)] italic">Not set</span>}</p>
            )}
          </Section>

          <div id="item-quality-estimate" className="scroll-mt-24">
          <Section label="Item Effort">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-[var(--text-pri)] tabular-nums">
                {itemEffort.capacityUnits != null ? `${itemEffort.capacityUnits} CU` : <span className="text-[var(--text-ter)] italic font-normal">Not set</span>}
              </span>
              {itemEffort.capacityUnits != null && (
                <span className="text-xs text-[var(--text-ter)] tabular-nums">(~{itemEffort.hours}h)</span>
              )}
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              {describeItemEffortSource(itemEffort, item.altitudeLevel)}
            </p>
          </Section>
          </div>

          {/* Execution Profile — set by ATC agent, reflects how this work will be carried out */}
          {item.executionProfile && (
            <Section label="Execution Profile">
              <p className="text-xs text-[var(--text-ter)] mb-2">
                How this item is expected to be executed in a human+AI organization.
              </p>
              <ExecutionProfileChip profile={item.executionProfile as ExecutionProfile} />
            </Section>
          )}

          {/* Subject type — optional, collapsed by default unless a value is set */}
          {isFeatureEnabled(FEATURE_FLAGS.SUBJECT_TYPES) && (
            <SubjectTypeSection item={item} readOnly={readOnly} saveField={saveField} />
          )}

        </div>
      </SectionCard>

      <SectionCard
        sectionId="item-section-measure-reason"
        title="Measure and reason"
        subtitle="How you will know it worked, and why it matters now. Both count toward the item's quality grade."
      >
        <div className="space-y-5">
          <ItemTextField
            anchorId="item-quality-measure"
            label="How will we know it worked?"
            placeholder="The measure of success, e.g. support tickets about exports drop by half"
            value={item.evidenceOfSuccess ?? ''}
            readOnly={readOnly || !perms.core}
            onSave={(value) => saveField({ evidenceOfSuccess: value })}
          />
          <ItemTextField
            anchorId="item-quality-reason"
            label="Why this, and why now?"
            placeholder="The reason, e.g. three large customers asked for it this quarter"
            value={item.whyNow ?? ''}
            readOnly={readOnly || !perms.core}
            onSave={(value) => saveField({ whyNow: value })}
          />
        </div>
      </SectionCard>

      <SectionCard
        sectionId="item-section-access"
        title="Access"
        subtitle="Who can view this item based on workspace membership and cycle, track, or session participation."
      >
        <ItemVisibilitySection itemId={item.id} />
      </SectionCard>

      <SectionCard
        sectionId="item-section-exit-status"
        title="Exit status"
        subtitle="Defer or archive to take an item out of active lists. Retire does the same for an item added by mistake, with a reason, and keeps its history."
      >
        <div className="space-y-4">
          <Section label="Lifecycle status">
            <p className="text-xs text-[var(--text-ter)] mb-2">
              Archived and deferred items are usually filtered out of default inventory views.
            </p>
            {perms.core && !readOnly ? (
              <div className="flex flex-wrap gap-2">
                {(['active', 'deferred', 'archived'] as const).map((status) => {
                  const current = (itemExt.exit_status ?? 'active') as string;
                  const isSelected = current === status;
                  const label =
                    status === 'active' ? 'Active' : status === 'deferred' ? 'Deferred' : 'Archived';
                  return (
                    <button
                      key={status}
                      type="button"
                      disabled={isSelected}
                      onClick={() => {
                        if (!isSelected) saveField({ exit_status: status });
                      }}
                      className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] cursor-default'
                          : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--rule)] hover:text-[var(--text-pri)]'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-sec)]">
                Current status: <strong>{stateLabel}</strong>
              </p>
            )}
          </Section>

          {onRetireItem ? (
            <AuthorizationGate
              action="items.delete"
              resource={{
                type: 'item',
                id: item.id,
                createdBy: itemExt.created_by_user_id ?? item.createdBy,
              }}
            >
              <div className="pt-3 border-t border-[var(--rule)]">
                <p className="text-xs text-[var(--text-ter)] mb-2 max-w-xl">
                  <strong>Retire</strong> takes this item out of every list with a written reason. The record and its
                  history stay, and it can be taken back while nothing uses it. Items already in a pool, session, plan
                  or decision can't be retired — use <strong>Archived</strong> above instead.
                </p>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => onRetireItem()}
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors disabled:opacity-50 disabled:pointer-events-none dark:hover:text-[var(--coral)]"
                >
                  <Archive className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  Retire…
                </button>
              </div>
            </AuthorizationGate>
          ) : null}
        </div>
      </SectionCard>

      </div>{/* end classification */}

      {/* 2. Relationships — inspector actions (collections included) */}
      <div className={show('relationships') ? undefined : 'hidden'}>
      <SectionCard sectionId="item-section-relationships" title="Relationships" subtitle="Manage how this item relates to others.">
        <ItemRelationshipActions
          item={item}
          readOnly={readOnly}
          onDiscoverClick={() => setShowDiscoverySheet(true)}
          onClusterClick={canEditAnything ? () => setShowClusterModal(true) : undefined}
          onPackageClick={canEditAnything ? () => setShowPackageModal(true) : undefined}
          onExclusiveClick={canEditAnything ? () => setShowExclusiveModal(true) : undefined}
          onDependencyClick={canEditAnything ? () => setShowSequenceLinkModal(true) : undefined}
          onAggregationClick={canEditAnything ? () => setShowCompositionModal(true) : undefined}
          onBreakdownClick={canEditAnything ? () => setShowCompositionModal(true) : undefined}
          onContainsClick={canEditAnything ? () => setShowContainsModal(true) : undefined}
          onPrerequisiteClick={canEditAnything ? () => setShowSequenceLinkModal(true) : undefined}
          onElaborationClick={canEditAnything ? () => setShowElaborationModal(true) : undefined}
          onCollectionClick={canEditAnything ? () => setShowCollectionPicker(true) : undefined}
          onReframeClick={canEditAnything ? () => {
            setPendingCategoryType(undefined);
            setShowCreateFrameModal(true);
          } : undefined}
        />
      </SectionCard>
      </div>{/* end relationships */}

      {/* Lineage — identity over time (Lineage tab) */}
      <div className={show('lineage') ? undefined : 'hidden'}>
      <SectionCard sectionId="item-section-lineage">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[var(--text-ter)]">Identity over time — split, merge, retire, supersede, and promotion history.</p>
          {canEditAnything && (
            <button
              type="button"
              onClick={() => setShowProvenanceActionModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--slate-dim)] hover:border-[var(--slate)] transition-colors"
            >
              <GitBranch className="h-4 w-4" />
              Provenance actions
            </button>
          )}
        </div>
        <LineageSummary item={item} />
      </SectionCard>
      </div>{/* end lineage */}

      {/* Collection modal — same pattern as other relationship modals */}
      <AddToCollectionModal
        isOpen={showCollectionPicker}
        onClose={() => setShowCollectionPicker(false)}
        sourceItemId={item.id}
      />

      {/* 5. Values — tabbed per attribute */}
      <div className={show('values') ? undefined : 'hidden'}>
      <SectionCard sectionId="item-section-values" title="Values" subtitle="Directional Planning signals — informing prioritization, not committing to execution.">
        {/* Planning Maturity selector */}
        <div className="mb-4 flex items-start justify-between gap-4 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] px-4 py-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-0.5">Planning Maturity</p>
            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)] leading-snug">
              {PLANNING_MATURITY_DESCRIPTIONS[itemExt.planning_maturity as PlanningMaturity ?? 'directional']}
            </p>
          </div>
          {!readOnly ? (
            <select
              value={itemExt.planning_maturity ?? 'directional'}
              onChange={(e) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                updateItem(item.id, { planning_maturity: e.target.value as PlanningMaturity } as any);
              }}
              className="shrink-0 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-[var(--active-tint)]"
            >
              {PLANNING_MATURITY_ORDER.map((m) => (
                <option key={m} value={m}>{PLANNING_MATURITY_LABELS[m]}</option>
              ))}
            </select>
          ) : (
            <span className="shrink-0 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              {PLANNING_MATURITY_LABELS[itemExt.planning_maturity as PlanningMaturity ?? 'directional']}
            </span>
          )}
        </div>
        <ValuesTab itemId={item.id} readOnly={readOnly} canAccept={!readOnly} />
        <ImpactCallout itemId={item.id} />
      </SectionCard>

      {/* 6. Custom Fields — workspace-defined metadata fields */}
      <CustomFieldsSection
        itemId={item.id}
        metadata={itemExt.metadata}
        readOnly={readOnly}
        onSave={(updates) => saveField(updates as Record<string, unknown>)}
      />
      </div>{/* end values */}

      {/* Relationship Inspector Modals */}
      <CreateCanonicalClusterModal
        isOpen={showClusterModal}
        onClose={() => setShowClusterModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Cluster Created', message: 'Items grouped in cluster', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <CreatePackageModal
        isOpen={showPackageModal}
        onClose={() => setShowPackageModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Package Updated', message: 'Item added to package', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <CreateExclusiveModal
        isOpen={showExclusiveModal}
        onClose={() => setShowExclusiveModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Competing set updated', message: 'Items marked as mutually exclusive', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <AddContainsModal
        isOpen={showContainsModal}
        onClose={() => setShowContainsModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Contains Added', message: 'Hierarchy link created', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <AddSequenceLinkModal
        isOpen={showSequenceLinkModal}
        onClose={() => setShowSequenceLinkModal(false)}
        onCreated={() => {
          addNotification({
            type: 'success',
            title: 'Sequence link added',
            message: 'Sequence or condition relationship created',
            duration: 3000,
          });
          refreshRelationships();
          onSaved?.();
        }}
        anchorItemId={item.id}
      />
      <AddCompositionModal
        isOpen={showCompositionModal}
        onClose={() => setShowCompositionModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Composition added', message: 'Whole-part link created', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        anchorItemId={item.id}
      />
      <AddElaborationModal
        isOpen={showElaborationModal}
        onClose={() => setShowElaborationModal(false)}
        onCreated={() => {
          addNotification({ type: 'success', title: 'Elaboration Added', message: 'Elaboration link created', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <ProvenanceActionModal
        isOpen={showProvenanceActionModal}
        onClose={() => setShowProvenanceActionModal(false)}
        onComplete={() => {
          addNotification({ type: 'success', title: 'Lineage updated', message: 'Item lineage action completed', duration: 3000 });
          refreshRelationships();
          onSaved?.();
        }}
        sourceItemId={item.id}
      />
      <RelationshipDiscoverySheet
        isOpen={showDiscoverySheet}
        onClose={() => setShowDiscoverySheet(false)}
        item={item}
        onClusterClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowClusterModal(true); } : undefined}
        onPackageClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowPackageModal(true); } : undefined}
        onExclusiveClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowExclusiveModal(true); } : undefined}
        onDependencyClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowSequenceLinkModal(true); } : undefined}
        onPrerequisiteClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowSequenceLinkModal(true); } : undefined}
        onAggregationClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowCompositionModal(true); } : undefined}
        onBreakdownClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowCompositionModal(true); } : undefined}
        onContainsClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowContainsModal(true); } : undefined}
        onElaborationClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowElaborationModal(true); } : undefined}
        onCollectionClick={canEditAnything ? () => { setShowDiscoverySheet(false); setShowCollectionPicker(true); } : undefined}
      />

      {/* Modals */}
      {editingFrameId && (
        <EditFrameModal
          candidateId={item.id}
          frameId={editingFrameId}
          isOpen={!!editingFrameId}
          onClose={() => setEditingFrameId(null)}
          onSaved={onSaved}
          onSetActive={() => {
            // setCanonicalFrame is already called inside EditFrameModal.
            // Do not overwrite itemCategory — the original frame is preserved.
          }}
        />
      )}

      <CreateFrameModal
        candidateId={item.id}
        candidateTitle={item.title}
        candidateDescription={item.description}
        currentType={item.itemCategory}
        initialType={pendingCategoryType}
        isOpen={showCreateFrameModal}
        onClose={() => { setShowCreateFrameModal(false); setPendingCategoryType(undefined); }}
        onCreated={(_frameId, _frameType, _isCanonical) => {
          // The addFrame store already handles canonicalFrameId. We must NOT
          // overwrite itemCategory here — the original frame is preserved as a
          // secondary framing. effectiveFrameTypeForAltitude reads the active
          // frame from canonicalFrameId, so the altitude UI updates correctly.
          setPendingCategoryType(undefined);
          addNotification({ type: 'success', title: 'Category Added', message: 'An additional category has been added', duration: 3000 });
        }}
      />

      {/* Category guide */}
      <InfoModal
        isOpen={showCategoryGuide}
        onClose={() => setShowCategoryGuide(false)}
        title="Item Categories"
        subtitle="How to categorize an item: choose the category that best describes what it is"
        sections={CATEGORY_GUIDE_SECTIONS}
        footerNote="An item can only have one active category, but you can add additional categories to describe it more than one way."
      />

      {/* Altitude guide */}
      <InfoModal
        isOpen={showAltitudeGuide}
        onClose={() => setShowAltitudeGuide(false)}
        title="Altitude Levels"
        subtitle="Altitude describes the scope and organizational elevation of an item"
        sections={ALTITUDE_GUIDE_SECTIONS}
        footerNote="Altitude is computed from three scoping questions: time horizon, work size, and impact surface. You can't set it directly — answer the questions and the level is inferred."
      />
    </div>
  );
}

export default ItemDetailEditable;
